import React, { useState } from 'react';

import { MCButton, MCText, Spinner } from 'src/components/basic';
import BaseComponent from 'src/components/BaseComponent';

import { lang, remCss } from 'src/helpers';
import { getStripePromise } from 'src/helpers/stripe';
import mainModel from './model';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';

const CheckoutForm = ({ hide, afterSave, confirmPaymentIntent, paymentElementOptions }) => {
  const stripe = useStripe();
  const elements = useElements();

  const [stripeError, setStripeError] = useState(null);
  const [loading, setLoading] = useState(null);
  const showFallbackError =
    !!stripeError && !['card_error', 'validation_error'].includes(stripeError.type);

  const handleSubmit = async (event) => {
    // We don't want to let default form submission happen here,
    // which would refresh the page.
    event.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    setStripeError(null);
    setLoading(true);
    console.log('handleSubmit stripe start', stripe, elements);
    const { error, paymentIntent } = await stripe.confirmPayment({
      //`Elements` instance that was used to create the Payment Element
      elements,
      confirmParams: {
        return_url: window.location.href
      },
      redirect: window.location.href.indexOf('test_redirect') !== -1 ? 'always' : 'if_required'
    });
    console.log('handleSubmit stripe confirmed', { error, paymentIntent });

    if (error) {
      // This point will only be reached if there is an immediate error when
      // confirming the payment. Show error to your customer (for example, payment
      // details incomplete)
      setLoading(false);
      setStripeError(error);
    } else {
      if (paymentIntent.status === 'succeeded' || paymentIntent.status === 'requires_capture') {
        mainModel.stripeList({
          cb: () => {
            mainModel.stripeActive({ id: paymentIntent.payment_method });
          }
        });
        if (confirmPaymentIntent) {
          console.log('handleSubmit confirming payment intent', paymentIntent);
          confirmPaymentIntent(paymentIntent, (err, data, success) => {
            setLoading(false);
            if (success) {
              if (afterSave) afterSave(err, data, success);
              else if (hide) hide();
            }
          });
        } else {
          setLoading(false);
          afterSave ? afterSave() : hide();
        }
      }
      // Your customer will be redirected to your `return_url`. For some payment
      // methods like iDEAL, your customer will be redirected to an intermediate
      // site first to authorize the payment, then redirected to the `return_url`.
    }
  };
  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement options={paymentElementOptions} />
      {showFallbackError && <div className={'mc-error mc-mt10'}>{stripeError.message}</div>}
      <div className='mc-modal-footer mc-flex' style={{ marginTop: remCss(20), borderTop: 'none' }}>
        <div className={'mc-grow'} />
        <MCButton
          primary
          dark
          radius
          medium
          disabled={loading || !stripe}
          loading={loading}
          type={'submit'}
          onClick={() => {}}
        >
          {loading ? lang.t('Processing') + '...' : lang.t('Submit')}
        </MCButton>
      </div>
    </form>
  );
};

class StripeForm extends BaseComponent {
  options = {
    // passing the client secret obtained from the server
    clientSecret: undefined
    // customerSessionClientSecret: 'cuss_secret_QebgT887BGsqoebWGyYvNAdw2g8hztubXnp1Dnw6wcFuHXS'
  };

  constructor() {
    super();
    this.state = {
      options: {
        clientSecret: undefined
      },
      stripeStatus: 'idle',
      stripeLoadError: null
    };
  }

  componentDidMount() {
    this.unmounted = false;
    this.getModel().subscribe(this.subscription);
    this.createPaymentIntent();
    this.ensureStripeLoaded();
  }

  getModel = () => this.props.model || mainModel;

  getOptions = () => this.props.options || this.state.options;

  componentDidUpdate(prevProps, prevState) {
    const prevClientSecret = (prevProps.options || prevState.options)?.clientSecret;
    const nextClientSecret = this.getOptions()?.clientSecret;

    if (prevClientSecret !== nextClientSecret) {
      this.ensureStripeLoaded();
    }
  }

  componentWillUnmount() {
    this.unmounted = true;
    this.getModel().unsubscribe(this.subscription);
  }

  ensureStripeLoaded = async () => {
    const options = this.getOptions();

    if (!options?.clientSecret) {
      if (this.state.stripeStatus !== 'idle' || this.state.stripeLoadError) {
        this.setState({ stripeStatus: 'idle', stripeLoadError: null });
      }
      return;
    }

    this.setState({ stripeStatus: 'loading', stripeLoadError: null });
    const stripePromise = getStripePromise();
    this.stripePromise = stripePromise;

    try {
      await stripePromise;

      if (this.unmounted || this.stripePromise !== stripePromise) {
        return;
      }

      this.setState({ stripeStatus: 'ready', stripeLoadError: null });
    } catch (error) {
      if (this.unmounted || this.stripePromise !== stripePromise) {
        return;
      }

      this.setState({
        stripeStatus: 'error',
        stripeLoadError: error?.message || 'Failed to load payment form. Please try again.'
      });
    }
  };

  retryStripeLoad = () => {
    this.ensureStripeLoaded();
  };

  createPaymentIntent = () => {
    let { createPaymentIntent } = this.props;
    if (createPaymentIntent) {
      this.props.createPaymentIntent((err, data, success) => {
        if (success) {
          this.setState({
            options: {
              clientSecret: data.clientSecret
            }
          });
        }
      });
    }
  };

  render() {
    const {
      description = '',
      amount = '',
      closeBtn,
      hide,
      afterSave,
      confirmPaymentIntent,
      paymentElementOptions,
      onlyContent = false,
      title = 'Add card'
    } = this.props;
    const { stripeStatus, stripeLoadError } = this.state;
    let options = this.getOptions();
    const stripePromise = options?.clientSecret ? this.stripePromise : null;
    const content = (
      <>
        {closeBtn && (
          <div className='mc-modal-header'>
            <div className='mc-popup-title'>{lang.t(title)}</div>
            {closeBtn}
          </div>
        )}
        {options?.clientSecret ? (
          stripeStatus === 'error' ? (
            <div>
              <div className={'mc-error mc-mt10'}>{stripeLoadError}</div>
              <div
                className='mc-modal-footer mc-flex'
                style={{ marginTop: remCss(20), borderTop: 'none' }}
              >
                <div className={'mc-grow'} />
                <MCButton primary dark radius medium type={'button'} onClick={this.retryStripeLoad}>
                  {lang.t('Retry')}
                </MCButton>
              </div>
            </div>
          ) : stripeStatus === 'ready' ? (
            <div>
              {(!!description || !!amount) && (
                <div className='mc-flex'>
                  {!!description && (
                    <div>
                      <MCText label={description} />
                    </div>
                  )}
                  <div className={'mc-grow'} />
                  {!!amount && (
                    <div>
                      <MCText primary label={amount} />
                    </div>
                  )}
                </div>
              )}
              <Elements stripe={stripePromise} options={options}>
                <CheckoutForm
                  hide={hide}
                  afterSave={afterSave}
                  confirmPaymentIntent={confirmPaymentIntent}
                  paymentElementOptions={paymentElementOptions}
                />
              </Elements>
            </div>
          ) : (
            <div>
              <Spinner />
            </div>
          )
        ) : (
          <div>
            <Spinner />
          </div>
        )}
      </>
    );

    if ((!closeBtn && !hide) || onlyContent) {
      return content;
    }
    return (
      <div className='mc-popup-contain' style={{ width: remCss(600), overflow: 'auto' }}>
        {content}
      </div>
    );
  }
}

export default StripeForm;
